import { optionalCustomerText } from '../../services/customerMapping';

export const BILLING_DETAILS_REQUIRED_TITLE = 'Billing details required';
export const BILLING_DETAILS_REQUIRED_MESSAGE =
  'This workspace is set to use billing details, but the required billing details are missing.';

export type BillingCustomerFields = {
  name?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  notes?: string | null;
  billingAddress?: string | null;
  billingEmail?: string | null;
  billingContact?: string | null;
};

export type WorkspaceBillingFlag = {
  use_billing_details_for_documents?: boolean | null;
};

export type DocumentCustomerIdentity = {
  usesBilling: boolean;
  valid: boolean;
  title: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string;
  email: string | null;
  phone: string | null;
  address: string;
  siteAddress: string;
};

const COMMERCIAL_KINDS = new Set(['quotation', 'estimate', 'invoice']);

export function isCommercialDocumentKind(kind?: string | null): boolean {
  return COMMERCIAL_KINDS.has(String(kind || '').trim().toLowerCase());
}

export function customerBillingAddress(customer?: BillingCustomerFields | null): string | null {
  if (!customer) return null;
  return optionalCustomerText(customer.billingAddress) || optionalCustomerText(customer.notes);
}

export function customerHasBillingDetails(customer?: BillingCustomerFields | null): boolean {
  if (!customer) return false;
  return !!(
    optionalCustomerText(customer.billingContact)
    || optionalCustomerText(customer.billingEmail)
    || customerBillingAddress(customer)
  );
}

export function customerHasRequiredBillingDetails(customer?: BillingCustomerFields | null): boolean {
  if (!customer) return false;
  return !!(
    optionalCustomerText(customer.billingContact)
    && optionalCustomerText(customer.billingEmail)
    && customerBillingAddress(customer)
  );
}

/** Initial default only. Never use this to overwrite a persisted workspace toggle. */
export function defaultUseBillingDetailsForDocuments(customer?: BillingCustomerFields | null): boolean {
  return customerHasBillingDetails(customer);
}

export function workspaceUsesBillingDetails(project?: WorkspaceBillingFlag | null): boolean {
  return project?.use_billing_details_for_documents === true;
}

export function commercialDocumentUsesBillingIdentity(args: {
  project?: WorkspaceBillingFlag | null;
  kind?: string | null;
}): boolean {
  if (!isCommercialDocumentKind(args.kind)) return false;
  return workspaceUsesBillingDetails(args.project);
}

export function workspaceBillingDetailsAreValid(args: {
  project?: WorkspaceBillingFlag | null;
  customer?: BillingCustomerFields | null;
}): boolean {
  if (!workspaceUsesBillingDetails(args.project)) return true;
  return customerHasRequiredBillingDetails(args.customer);
}

export function splitName(name: string | null | undefined): { firstName: string | null; lastName: string | null } {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: null, lastName: null };
  if (parts.length === 1) return { firstName: parts[0], lastName: null };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

export function resolveDocumentCustomerIdentity(args: {
  customer?: BillingCustomerFields | null;
  project?: WorkspaceBillingFlag | null;
  kind?: string | null;
}): DocumentCustomerIdentity {
  const customer = args.customer || null;
  const siteAddress = optionalCustomerText(customer?.address) || '';
  const usesBilling = commercialDocumentUsesBillingIdentity(args);

  if (!usesBilling) {
    const fullName = optionalCustomerText(customer?.name) || '';
    const names = splitName(fullName);
    return {
      usesBilling: false,
      valid: true,
      title: optionalCustomerText(customer?.title),
      firstName: names.firstName,
      lastName: names.lastName,
      fullName,
      email: optionalCustomerText(customer?.email),
      phone: optionalCustomerText(customer?.phone),
      address: siteAddress,
      siteAddress,
    };
  }

  const fullName = optionalCustomerText(customer?.billingContact) || '';
  const names = splitName(fullName);
  const address = customerBillingAddress(customer) || '';
  const email = optionalCustomerText(customer?.billingEmail);
  return {
    usesBilling: true,
    valid: customerHasRequiredBillingDetails(customer),
    title: null,
    firstName: names.firstName,
    lastName: names.lastName,
    fullName,
    email,
    phone: null,
    address,
    siteAddress,
  };
}

export function assertCanIssueCommercialDocument(args: {
  project?: WorkspaceBillingFlag | null;
  customer?: BillingCustomerFields | null;
  kind?: string | null;
}): void {
  if (!isCommercialDocumentKind(args.kind)) return;
  if (workspaceBillingDetailsAreValid(args)) return;
  throw new Error(BILLING_DETAILS_REQUIRED_MESSAGE);
}
